/**
 * CUTICLE CARE CARD COMPONENT
 *
 * Features:
 * - Interactive icon toggles at the bottom (Lucide icons)
 * - Usage meter bar showing how many times item has been used
 * - Collection/Event info display with click-to-filter
 * - Tags display with click-to-filter
 * - Selection mode support for bulk actions
 * - Click to open detail modal
 * - Emerald color scheme
 * - Grayed-out appearance when destashed
 */

import React, { useState, useEffect } from 'react';
import { AppIcons } from '../../constants/icons';
import { CategoryColors, PriorityColors, type PriorityKey } from '../../constants/categoryColors';
import type { CollectionItem } from '../../types';
import DestashStatusBadge from '../DestashStatusBadge';
import ReleaseInfoChips from '../common/ReleaseInfoChips';
import PrivateLockBadge from '../common/PrivateLockBadge';

interface CuticleCareCardProps {
  cuticleItem: CollectionItem;
  onUpdate: (id: string, updates: Partial<CollectionItem>) => Promise<void>;
  onClick?: () => void;
  userId: string;
  isSelectionMode?: boolean;
  isSelected?: boolean;
  onSelect?: (itemId: string) => void;
  onFilterByTag?: (tagCategory: string, tagValue: string) => void;
  onFilterByCollection?: (collectionName: string) => void;
  onFilterByEvent?: (eventSource: string) => void;
}

const CuticleCareCard: React.FC<CuticleCareCardProps> = ({
  cuticleItem,
  onUpdate,
  onClick,
  userId,
  isSelectionMode = false,
  isSelected = false,
  onSelect,
  onFilterByTag,
  onFilterByCollection,
  onFilterByEvent
}) => {

  const [onWishlist, setOnWishlist] = useState(cuticleItem.onWishlist || false);
  const [isFavorite, setIsFavorite] = useState(cuticleItem.isFavorite || false);
  const [isRareLE, setIsRareLE] = useState(cuticleItem.isRareLE || false);
  const [needsInfo, setNeedsInfo] = useState(cuticleItem.needsInfo || false);

  useEffect(() => {
    setOnWishlist(cuticleItem.onWishlist || false);
    setIsFavorite(cuticleItem.isFavorite || false);
    setIsRareLE(cuticleItem.isRareLE || false);
    setNeedsInfo(cuticleItem.needsInfo || false);
  }, [cuticleItem.onWishlist, cuticleItem.isFavorite, cuticleItem.isRareLE, cuticleItem.needsInfo]);

  const handleToggle = async (field: 'onWishlist' | 'needsInfo' | 'isFavorite' | 'isRareLE', e: React.MouseEvent) => {
    e.stopPropagation();
    if (isSelectionMode) return;

    let currentValue = false;
    switch (field) {
      case 'onWishlist': currentValue = onWishlist; break;
      case 'isFavorite': currentValue = isFavorite; break;
      case 'isRareLE': currentValue = isRareLE; break;
      case 'needsInfo': currentValue = needsInfo; break;
    }
    const newValue = !currentValue;

    switch (field) {
      case 'onWishlist': setOnWishlist(newValue); break;
      case 'isFavorite': setIsFavorite(newValue); break;
      case 'isRareLE': setIsRareLE(newValue); break;
      case 'needsInfo': setNeedsInfo(newValue); break;
    }

    if (field === 'onWishlist' && newValue) {
      await onUpdate(cuticleItem.id, { [field]: newValue, isoPriority: cuticleItem.isoPriority || 'iso' });
    } else {
      await onUpdate(cuticleItem.id, { [field]: newValue });
    }
  };

  const handleCardClick = () => {
    if (isSelectionMode && onSelect) {
      onSelect(cuticleItem.id);
    } else if (onClick) {
      onClick();
    }
  };

  const handleTagClick = (category: string, value: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (onFilterByTag) onFilterByTag(category, value);
  };

  const isPhotoHidden = (photo: any): boolean => {
    return typeof photo === 'object' && photo.dmcaStatus === 'hidden';
  };

  const getDisplayImage = (): string | null => {
    if (cuticleItem.photos && cuticleItem.photos.length > 0) {
      const defaultPhoto = cuticleItem.photos.find(p => p.isDefault && !isPhotoHidden(p));
      if (defaultPhoto) return defaultPhoto.url;
      const visiblePhoto = cuticleItem.photos.find(p => !isPhotoHidden(p));
      if (visiblePhoto) return visiblePhoto.url;
    }
    if (cuticleItem.photoUrl) return cuticleItem.photoUrl;
    return null;
  };

  const firstImage = getDisplayImage();
  const timesUsed = cuticleItem.timesUsed || 0;

  // Usage bar config - emerald theme for cuticle care
  let usageLevel = 'Unused';
  let usagePercentage = 0;
  let usageColor = 'bg-gray-300';
  let usageBarColor = 'bg-gray-400';

  if (isFavorite || timesUsed >= 7) {
    usageLevel = 'Essential';
    usagePercentage = 100;
    usageColor = 'bg-emerald-100';
    usageBarColor = 'bg-gradient-to-r from-emerald-400 to-green-400';
  } else if (timesUsed >= 3) {
    usageLevel = 'Regular';
    usagePercentage = 60;
    usageColor = 'bg-emerald-100';
    usageBarColor = 'bg-gradient-to-r from-emerald-400 to-teal-400';
  } else if (timesUsed >= 1) {
    usageLevel = 'Tried';
    usagePercentage = 20;
    usageColor = 'bg-green-100';
    usageBarColor = 'bg-gradient-to-r from-green-300 to-emerald-400';
  }

  const getItemDescription = (): string | null => {
    const parts: string[] = [];
    if (cuticleItem.cuticleCareType) parts.push(cuticleItem.cuticleCareType);
    if (cuticleItem.size) parts.push(cuticleItem.size);
    if (cuticleItem.color) parts.push(cuticleItem.color); // scent stored in color field
    return parts.length > 0 ? parts.join(' • ') : null;
  };

  const itemDescription = getItemDescription();

  const displayTags: { category: string; value: string; label: string }[] = [];
  if (cuticleItem.tags && cuticleItem.tags.length > 0) {
    cuticleItem.tags.forEach((tag: string) => {
      displayTags.push({ category: 'tags', value: tag, label: tag });
    });
  }

  const getTagColor = (): string => {
    return CategoryColors.cuticle_care.bgMuted + ' ' + CategoryColors.cuticle_care.bgHover;
  };

  return (
    <div
      onClick={handleCardClick}
      className={`card-light overflow-hidden hover:shadow-2xl transform hover:-translate-y-1 transition-all cursor-pointer flex flex-col ${
        isSelectionMode && isSelected ? 'ring-4 ring-blue-500' : ''
      } ${cuticleItem.isDestashed ? 'opacity-100 grayscale' : ''}`}
    >
      {/* Photo with Selection Overlay */}
      <div className="relative">
        {firstImage ? (
          <img
            src={firstImage}
            alt={`${cuticleItem.brand} ${cuticleItem.name}`}
            className="w-full h-64 object-cover"
          />
        ) : (
          <div className="w-full h-64 bg-gradient-to-br from-emerald-400 to-green-600 flex items-center justify-center">
            <AppIcons.CuticleCare className="w-16 h-16 text-white" aria-label="Cuticle Care" />
          </div>
        )}

        {/* Selection Overlay & Checkbox */}
        {isSelectionMode && (
          <>
            {isSelected && (
              <div className="absolute inset-0 bg-blue-500 bg-opacity-30 flex items-center justify-center">
                <div className="w-20 h-20 bg-blue-500 rounded-full flex items-center justify-center">
                  <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              </div>
            )}
            <div className="absolute top-2 left-2">
              <div className={`w-6 h-6 rounded border-2 flex items-center justify-center ${
                isSelected ? 'bg-blue-500 border-blue-500' : 'bg-white border-gray-400'
              }`}>
                {isSelected && (
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </div>
            </div>
          </>
        )}

        {/* Type Badge */}
        <div className="absolute top-2 right-2">
          <span className={`px-2 py-1 ${CategoryColors.cuticle_care.badge} rounded-full text-xs font-bold font-body`}>
            {cuticleItem.cuticleCareType || 'Cuticle Care'}
          </span>
        </div>

        {/* ISO Priority Badge */}
        {!isSelectionMode && cuticleItem.onWishlist && (
          <div className="absolute top-2 left-2">
            <span className={`px-2 py-1 rounded-full text-xs font-bold text-white font-body ${
              PriorityColors[cuticleItem.isoPriority as PriorityKey]?.bg || PriorityColors.iso.bg
            }`}>
              {PriorityColors[cuticleItem.isoPriority as PriorityKey]?.label || PriorityColors.iso.label}
            </span>
          </div>
        )}

        {/* Private lock — owner-only marker; suppressed in selection mode
            so it doesn't fight the selection checkbox in the top-left. */}
        {!isSelectionMode && cuticleItem.isPrivate && <PrivateLockBadge />}
      </div>

      {/* Info */}
      <div className="p-4 flex flex-col flex-1">
        {/* Top Section */}
        <div className="mb-2">
          <h3 className={`text-xs uppercase ${CategoryColors.cuticle_care.text} font-bold tracking-wide mb-1 font-heading`}
            style={{ color: '#059669' }}>
            {cuticleItem.brand}
          </h3>
          <h4 className="text-lg font-bold text-light-primary leading-tight font-body">
            {cuticleItem.name}
          </h4>
        </div>

        {/* Item Description */}
        {itemDescription && (
          <p className="text-sm text-light-secondary mb-2 line-clamp-2 font-body">
            {itemDescription}
          </p>
        )}

        {/* Release info chips (release type, collection, event, season/year) */}
        <ReleaseInfoChips
          item={cuticleItem as any}
          onFilterByCollection={onFilterByCollection}
          onFilterByEvent={onFilterByEvent}
        />

        {/* Tags */}
        {displayTags.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-2">
            {displayTags.map((tag, index) => (
              <button
                key={`${tag.category}-${tag.value}-${index}`}
                onClick={(e) => handleTagClick(tag.category, tag.value, e)}
                className={`px-2 py-1 ${getTagColor()} text-white rounded text-xs font-bold transition-default font-body ${
                  onFilterByTag ? 'cursor-pointer' : 'cursor-default'
                }`}
                title={`Filter by: ${tag.label}`}
              >
                {tag.label}
              </button>
            ))}
          </div>
        )}

        {/* Quantity Badge */}
        <div className="mb-2">
          <span className="inline-block px-2 py-0.5 bg-gray-200 text-gray-600 rounded text-xs font-medium font-body">
            Qty: {cuticleItem.quantity || 1}
          </span>
        </div>

        {/* Destash Status Badge */}
        {cuticleItem.isForDestash && (
          <div className="mb-2">
            <DestashStatusBadge
              itemId={cuticleItem.id}
              userId={userId}
              currentStatus={cuticleItem.destashStatus || 'available'}
              isForDestash={cuticleItem.isForDestash}
              quantity={cuticleItem.quantity || 1}
              destashQuantityAvailable={cuticleItem.destashQuantityAvailable}
            />
          </div>
        )}

        <div className="flex-1"></div>

        {/* Usage Bar */}
        {!onWishlist && (
          <div className="mb-3">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-bold text-light-secondary font-body">{usageLevel}</span>
              <span className="text-xs font-bold text-light-secondary font-body flex items-center gap-1">
                {timesUsed}x {isFavorite && <AppIcons.Favorite className="w-3 h-3 text-yellow-500 fill-yellow-500" aria-label="Favorite" />}
              </span>
            </div>
            <div className={`w-full h-2 rounded-full ${usageColor} overflow-hidden`}>
              <div
                className={`h-full ${usageBarColor} transition-slow rounded-full`}
                style={{ width: `${usagePercentage}%` }}
              ></div>
            </div>
          </div>
        )}

        <div className="border-t border border-gray-300 mb-3"></div>

        {/* Icon Toggles */}
        <div className="flex items-center justify-center gap-4">
          <button
            onClick={(e) => handleToggle('onWishlist', e)}
            disabled={isSelectionMode}
            className={`transition-all transform hover:scale-125 ${isSelectionMode ? 'opacity-40 cursor-not-allowed' : ''} ${onWishlist ? 'text-amber-500' : 'text-light-muted opacity-40'}`}
            title="Wishlist"
            aria-label="Wishlist"
          >
            <AppIcons.Wishlist className={`w-6 h-6 text-current ${onWishlist ? 'fill-amber-500 text-amber-500' : ''}`} />
          </button>

          <button
            onClick={(e) => handleToggle('isFavorite', e)}
            disabled={isSelectionMode}
            className={`transition-all transform hover:scale-125 ${isSelectionMode ? 'opacity-40 cursor-not-allowed' : ''} ${isFavorite ? 'text-pink-500' : 'text-light-muted opacity-40'}`}
            title="Favorite"
            aria-label="Favorite"
          >
            <AppIcons.Favorite className={`w-6 h-6 text-current ${isFavorite ? 'fill-pink-500' : ''}`} />
          </button>

          <button
            onClick={(e) => handleToggle('isRareLE', e)}
            disabled={isSelectionMode}
            className={`transition-all transform hover:scale-125 ${isSelectionMode ? 'opacity-40 cursor-not-allowed' : ''} ${isRareLE ? 'text-purple-500' : 'text-light-muted opacity-40'}`}
            title="Rare/Limited Edition"
            aria-label="Rare/Limited Edition"
          >
            <AppIcons.RareLE className={`w-6 h-6 ${isRareLE ? 'fill-purple-500' : ''}`} />
          </button>

          <button
            onClick={(e) => handleToggle('needsInfo', e)}
            disabled={isSelectionMode}
            className={`transition-all transform hover:scale-125 ${isSelectionMode ? 'opacity-40 cursor-not-allowed' : ''} ${needsInfo ? 'text-blue-500' : 'text-light-muted opacity-40'}`}
            title="Needs Info"
            aria-label="Needs Info"
          >
            <AppIcons.NeedsInfo className={`w-6 h-6 text-current ${needsInfo ? 'fill-blue-500' : ''}`} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default CuticleCareCard;
